import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { zhTW } from "date-fns/locale";
import { useNavigate, useParams } from "react-router-dom";
import { MdError, MdKeyboardArrowLeft, MdPlayArrow } from "react-icons/md";
import { FiCalendar, FiTag, FiTrash2 } from "react-icons/fi";
import Datepicker from "../Utilities/Datepicker/Datepicker";
import Button from "../Utilities/Button";
import Switcher from "../Utilities/AlertSwitch/Switcher";
import TextField from "../Utilities/TextField";
import Toast from "../Utilities/Toast";
import Alert from "../Utilities/Alert";
import Modal from "../Utilities/Modal";
import Dropdown from "../Utilities/DropdownDynamic";
import RightPanelLayout from "../Layout/RightPanelLayout";
import EventRichTextEditor from "./EventRichTextEditor";
import SplitHourMinuteTimeInput from "./SplitHourMinuteTimeInput";
import EventStatusBadge from "./EventStatusBadge";
import styles from "./style/EventFormPage.module.scss";
import {
  getHighlightPublishMissingFields,
  getHighlightSaveErrorToast,
  normalizeMetaJson,
  toApiDatetime,
} from "./activityTransforms";
import {
  createActivityHighlight,
  deleteActivityHighlight,
  fetchActivityHighlightById,
  updateActivityHighlight,
} from "./activitiesApi";

const EVENT_TYPE_LABELS = ["分享會", "發表會", "工作坊"];

const EVENT_TYPE_LABEL_TO_META = {
  分享會: "sharing",
  發表會: "launch",
  工作坊: "workshop",
};

const META_TO_EVENT_TYPE_LABEL = {
  sharing: "分享會",
  launch: "發表會",
  workshop: "工作坊",
};

const buildHighlightAssets = (videoUrl, imageAssets) => {
  const trimmed = String(videoUrl || "").trim();
  const images = (imageAssets || []).filter((a) => a?.media_type === "image");
  const next = images.map((a, i) => ({
    media_type: "image",
    url: a.url,
    thumbnail_url: a.thumbnail_url,
    caption: a.caption,
    sort_order: a.sort_order != null ? a.sort_order : i,
    ...(a.meta_json != null ? { meta_json: a.meta_json } : {}),
  }));
  if (trimmed) {
    next.push({
      media_type: "video",
      url: trimmed,
      sort_order: next.length,
    });
  }
  return next;
};

const stripHtmlToPlain = (html) => {
  const plain = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain;
};

const extractYouTubeVideoId = (urlOrId) => {
  const raw = String(urlOrId || "").trim();
  if (!raw) return null;
  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return raw;

  const patterns = [
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/.*[?&]v=([a-zA-Z0-9_-]{11})/,
  ];

  for (const re of patterns) {
    const match = raw.match(re);
    if (match && match[1]) return match[1];
  }
  return null;
};

const EventHighlightFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [detailLoading, setDetailLoading] = useState(mode === "edit");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [toolbarSaveWarning, setToolbarSaveWarning] = useState("");
  const [publishSwitchWarning, setPublishSwitchWarning] = useState("");
  const [imageAssetsOnly, setImageAssetsOnly] = useState([]);
  const [baseMetaFromApi, setBaseMetaFromApi] = useState({});
  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const initialForm = useMemo(
    () => ({
      title: "",
      eventType: "",
      videoUrl: "",
      subtitle: "",
      description: "",
      isPublished: false,
      publishedAt: "",
    }),
    [],
  );

  const {
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({ defaultValues: initialForm });
  const form = watch();

  const toLocalDatetimeInput = (value) => {
    if (!value) return "";
    if (value.includes("T")) return value.slice(0, 16);
    const parts = value.split(" ");
    if (parts.length !== 2) return "";
    return `${parts[0]}T${String(parts[1] || "").slice(0, 5)}`;
  };

  const handlePublishedDatePick = (date) => {
    const prev = getValues();
    if (!date) {
      setValue("publishedAt", "", { shouldDirty: true });
      return;
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const t =
      prev.publishedAt && prev.publishedAt.length >= 16
        ? prev.publishedAt.slice(11, 16)
        : "10:00";
    setValue("publishedAt", `${yyyy}-${mm}-${dd}T${t}`, { shouldDirty: true });
  };

  const onPublishedTimeCommit = (hhmm) => {
    const t = hhmm || "00:00";
    const prev = getValues();
    const date =
      prev.publishedAt && prev.publishedAt.length >= 10
        ? prev.publishedAt.slice(0, 10)
        : new Date().toISOString().slice(0, 10);
    setValue("publishedAt", `${date}T${t}`, { shouldDirty: true });
  };

  const setField = (key, value) => {
    setValue(key, value, { shouldDirty: true });
    if (value !== "" && value != null) {
      clearErrors(key);
    }
  };

  useEffect(() => {
    if (mode !== "edit" || !id) {
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    fetchActivityHighlightById(id)
      .then((data) => {
        if (cancelled) return;
        const assets = Array.isArray(data?.assets) ? data.assets : [];
        const images = assets.filter((a) => a?.media_type === "image");
        setImageAssetsOnly(images);
        const videoAsset = assets.find((a) => a?.media_type === "video");
        const meta = normalizeMetaJson(data?.meta_json);
        setBaseMetaFromApi(meta);
        reset({
          title: data?.title ?? "",
          eventType: META_TO_EVENT_TYPE_LABEL[meta.event_type] ?? "",
          videoUrl: videoAsset?.url ?? "",
          subtitle: typeof meta.subtitle === "string" ? meta.subtitle : "",
          description: data?.description ?? "",
          isPublished: Boolean(data?.is_published),
          publishedAt: toLocalDatetimeInput(data?.published_at),
        });
        clearErrors();
      })
      .catch(() => {
        if (!cancelled) navigate("/dashboard/tdp/events/highlights");
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, id, reset, clearErrors, navigate]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setIsConfirmLeaveOpen(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirty]);

  const publishSegmentText = form.isPublished ? "已上架" : "未上架";
  const publishSegmentTheme = form.isPublished ? "published" : "draft";

  const buildMetaPayload = () => {
    const merged = { ...baseMetaFromApi };
    const typeKey = EVENT_TYPE_LABEL_TO_META[form.eventType];
    if (typeKey) {
      merged.event_type = typeKey;
    } else {
      delete merged.event_type;
    }
    if (form.subtitle?.trim()) {
      merged.subtitle = form.subtitle.trim();
    } else {
      delete merged.subtitle;
    }
    return Object.keys(merged).length ? merged : undefined;
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    clearErrors();
    setToolbarSaveWarning("");
    setPublishSwitchWarning("");

    if (!form.isPublished) {
      if (!form.title.trim()) {
        setError("title", { type: "manual", message: "此欄位為必填" });
        setToolbarSaveWarning("未輸入標題");
        return;
      }
    } else {
      const missing = getHighlightPublishMissingFields({
        title: form.title,
        eventType: form.eventType,
        description: form.description,
      });
      missing.forEach((field) =>
        setError(field, { type: "manual", message: "此欄位為必填" }),
      );
      if (missing.length > 0) {
        setPublishSwitchWarning("尚有未完成的內容");
        return;
      }
    }

    const metaPayload = buildMetaPayload();
    const assetsPayload = buildHighlightAssets(form.videoUrl, imageAssetsOnly);

    const payload = {
      title: form.title.trim(),
      description: form.description?.trim() || undefined,
      is_published: Boolean(form.isPublished),
      published_at: form.publishedAt ? toApiDatetime(form.publishedAt) : undefined,
      ...(metaPayload ? { meta_json: metaPayload } : {}),
      assets: assetsPayload,
    };

    setIsSubmitting(true);
    try {
      if (mode === "new") {
        await createActivityHighlight(payload);
      } else {
        await updateActivityHighlight(id, payload);
      }
      reset(form);
      const highlightName = form.title.trim() || "未命名花絮";
      setToastState({
        show: true,
        title:
          mode === "new"
            ? `已新增活動花絮「${highlightName}」。`
            : `已儲存活動花絮「${highlightName}」。`,
        subtitle: "",
        error: false,
      });
      window.setTimeout(() => {
        setToastState((s) => ({ ...s, show: false }));
        navigate("/dashboard/tdp/events/highlights");
      }, 1400);
    } catch (err) {
      const toast = getHighlightSaveErrorToast(err);
      setToastState({
        show: true,
        title: toast.title,
        subtitle: "",
        error: toast.error,
      });
      window.setTimeout(() => {
        setToastState((s) => ({ ...s, show: false }));
      }, 3200);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === "edit" && id && detailLoading) {
    return (
      <RightPanelLayout title="活動花絮">
        <div className={styles.emptyMessage}>載入中…</div>
      </RightPanelLayout>
    );
  }

  return (
    <RightPanelLayout title="活動花絮">
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className={styles.topToolbarHeader}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => {
              if (!isDirty) {
                navigate("/dashboard/tdp/events/highlights");
                return;
              }
              setIsConfirmLeaveOpen(true);
            }}
          >
            <MdKeyboardArrowLeft size={32} />
            返回
          </button>
          <div className={styles.rightActions}>
            {toolbarSaveWarning ? (
              <span className={styles.toolbarWarningText}>
                <MdError size={22} aria-hidden />
                {toolbarSaveWarning}
              </span>
            ) : null}
            <EventStatusBadge theme={publishSegmentTheme} text={publishSegmentText} />
            <button
              type="button"
              className={styles.previewButton}
              onClick={() => setIsPreviewOpen(true)}
            >
              預覽
            </button>
            <Button
              type="primary"
              title={isSubmitting ? "儲存中…" : "儲存"}
              disabled={isSubmitting}
            />
            {mode === "edit" ? (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => setIsDeleteOpen(true)}
                aria-label="刪除花絮"
              >
                <FiTrash2 size={24} aria-hidden />
                刪除花絮
              </button>
            ) : null}
          </div>
        </div>

        <div className={styles.formSheet}>
          <div className={styles.columnStack}>
            <div className={`${styles.editableBlock} ${styles.editableBlockTitleOnly}`}>
              <div className={styles.titleFieldRow}>
                <div className={styles.activityTitleLabel}>
                  文章標題
                  <span className={styles.requiredPrimary}>*</span>
                </div>
                <div className={styles.titleFieldInputWrap}>
                  <TextField
                    placeholder="請輸入"
                    width="100%"
                    fullWidth
                    value={form.title}
                    error={Boolean(errors.title)}
                    onChange={(v) => setField("title", v)}
                  />
                </div>
              </div>
            </div>

            <div className={styles.editableBlock}>
              <div className={styles.infoRow}>
                <div className={styles.infoRowLabel}>活動類型</div>
                <div className={styles.infoRowBody}>
                  <Dropdown
                    placeholder="請選擇"
                    value={form.eventType}
                    listItems={EVENT_TYPE_LABELS}
                    error={Boolean(errors.eventType)}
                    fitContent
                    color="#333333"
                    onChange={(item) => {
                      setPublishSwitchWarning("");
                      setField("eventType", item);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={styles.sectionTitle}>上架設定</div>
            <div className={styles.editableBlock}>
              <div className={styles.infoRow}>
                <div className={styles.infoRowLabel}>上架</div>
                <div className={styles.infoRowBody}>
                  <div className={styles.switchRow}>
                    <Switcher
                      isChecked={form.isPublished}
                      onChange={() => {
                        setPublishSwitchWarning("");
                        setField("isPublished", !form.isPublished);
                      }}
                      color={{ on: "#5856D6", off: "#5856D64D" }}
                      width={33}
                      height={20}
                    />
                    <span className={styles.switchHint}>設定上架時間（非必填）</span>
                    {publishSwitchWarning ? (
                      <span className={styles.publishInlineWarning}>
                        <MdError size={22} aria-hidden /> {publishSwitchWarning}
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.activityTimeControls}>
                    <div className={styles.activityDatePickerWrap}>
                      <Datepicker
                        selectDate={
                          form.publishedAt
                            ? new Date(`${form.publishedAt.slice(0, 10)}T12:00:00`)
                            : ""
                        }
                        setSelectDate={handlePublishedDatePick}
                        error={Boolean(errors.publishedAt)}
                        iconType="arrow"
                        placeholderText="選擇日期"
                        dateFormat="yyyy-MM-dd"
                        locale={zhTW}
                      />
                    </div>
                    <SplitHourMinuteTimeInput
                      value={form.publishedAt?.slice(11, 16) || "10:00"}
                      onCommit={onPublishedTimeCommit}
                      ariaLabel="上架時間"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.sectionTitle}>文章內容</div>
            <div className={styles.editableBlock}>
              <div className={styles.infoRow}>
                <div className={styles.infoRowLabel}>影片網址</div>
                <div className={styles.infoRowBody}>
                  <TextField
                    placeholder="請輸入"
                    width="100%"
                    fullWidth
                    value={form.videoUrl}
                    onChange={(v) => setField("videoUrl", v)}
                  />
                </div>
              </div>
            </div>
            <div className={styles.editableBlock}>
              <div className={styles.infoRow}>
                <div className={styles.infoRowLabel}>副標題</div>
                <div className={styles.infoRowBody}>
                  <TextField
                    placeholder="請輸入"
                    width="100%"
                    fullWidth
                    value={form.subtitle}
                    onChange={(v) => setField("subtitle", v)}
                  />
                </div>
              </div>
            </div>
            <div className={styles.editableBlock}>
              <div className={`${styles.infoRow} ${styles.infoRowAlignStart}`}>
                <div className={styles.infoRowLabel}>內文介紹</div>
                <div className={styles.infoRowBody}>
                  <div
                    className={errors.description ? styles.richTextErrorWrap : undefined}
                  >
                    <EventRichTextEditor
                      value={form.description}
                      onChange={(html) => setField("description", html)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <Alert
        isOpen={isConfirmLeaveOpen}
        onClose={() => setIsConfirmLeaveOpen(false)}
        onConfirm={() => {
          setIsConfirmLeaveOpen(false);
          navigate("/dashboard/tdp/events/highlights");
        }}
        leftButtonText="留在此頁"
        rightButtonText="離開"
        mainTitle="尚未儲存，確定離開？"
        subTitle="離開後本次填寫的內容將不會保留。"
        isWarning
      />

      <Alert
        isOpen={mode === "edit" && isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={async () => {
          setIsDeleteOpen(false);
          try {
            await deleteActivityHighlight(id);
            setToastState({
              show: true,
              title: "已刪除活動花絮",
              subtitle: "",
              error: false,
            });
            window.setTimeout(() => {
              setToastState((s) => ({ ...s, show: false }));
              navigate("/dashboard/tdp/events/highlights");
            }, 900);
          } catch {
            setToastState({
              show: true,
              title: "刪除失敗請重試",
              subtitle: "",
              error: true,
            });
          }
        }}
        leftButtonText="取消"
        rightButtonText="刪除花絮"
        mainTitle="確認刪除花絮？"
        subTitle="刪除後無法復原。"
        isWarning
      />

      <Modal
        isOpen={isPreviewOpen}
        size="xl"
        width="min(720px, calc(100vw - 32px))"
      >
        <div className={styles.highlightPreviewModalRoot}>
          <header className={styles.highlightPreviewHeader}>
            <h2 className={styles.highlightPreviewHeaderTitle}>預覽</h2>
            <button
              type="button"
              className={styles.highlightPreviewCloseButton}
              onClick={() => setIsPreviewOpen(false)}
              aria-label="關閉預覽"
            >
              ×
            </button>
          </header>

          <div className={styles.highlightPreviewDarkBlock}>
            {(() => {
              const previewDate =
                form.publishedAt && form.publishedAt.length
                  ? (() => {
                      const d = new Date(form.publishedAt);
                      return Number.isNaN(d.getTime())
                        ? null
                        : format(d, "yyyy.MM.dd", { locale: zhTW });
                    })()
                  : null;
              const dateText = previewDate || "未設定";
              const typeTag = form.eventType || "未設定";

              const videoId = extractYouTubeVideoId(form.videoUrl);
              const thumbnailUrl = videoId
                ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
                : "";

              const plainDescription = stripHtmlToPlain(form.description);

              const titleText = form.title.trim() || "未命名花絮";
              const subtitleText = form.subtitle?.trim() || "未設定副標題";

              return (
                <>
                  <div className={styles.highlightPreviewVideoWrap}>
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt="YouTube 封面"
                        className={styles.highlightPreviewVideoImg}
                      />
                    ) : (
                      <div className={styles.highlightPreviewVideoPlaceholder}>
                        請輸入 YouTube 影片連結
                      </div>
                    )}
                    <div className={styles.highlightPreviewVideoOverlay}>
                      <div className={styles.highlightPreviewPlayCircle}>
                        <MdPlayArrow size={26} aria-hidden />
                      </div>
                    </div>
                  </div>

                  <div className={styles.highlightPreviewLayers}>
                    <div className={styles.highlightPreviewLayer1}>
                      <span className={styles.highlightPreviewMetaItem}>
                        <FiCalendar size={16} aria-hidden />
                        {dateText}
                      </span>
                      <span className={styles.highlightPreviewTypeTag}>
                        {typeTag}
                      </span>
                      <span className={styles.highlightPreviewMetaItem}>
                        <FiTag size={16} aria-hidden />
                        影片
                      </span>
                    </div>

                    <h3 className={styles.highlightPreviewTitle}>
                      {titleText}
                    </h3>
                    <p className={styles.highlightPreviewSubtitle}>
                      {subtitleText}
                    </p>
                    <p className={styles.highlightPreviewDescription}>
                      {plainDescription || "請輸入花絮內文"}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </Modal>

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default EventHighlightFormPage;
